import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Crown, Target } from "lucide-react";
import PredictionStreakLeaderboard from "@/components/PredictionStreakLeaderboard";
import PredictionBadges from "@/components/PredictionBadges";
import WeeklyTopPredictors from "@/components/WeeklyTopPredictors";

export default function Predictions() {
  const { user } = useAuth();
  const isAuthed = user && user !== false;
  const [board, setBoard] = useState([]);
  const [mine, setMine] = useState(null);
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("all"); // all | exact | hit | miss | pending
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/predictions/leaderboard").then((r) => setBoard(r.data));
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/fixtures").then((r) => setFixtures(r.data));
    if (isAuthed) {
      api.get("/me/predictions").then((r) => setMine(r.data));
    }
  }, [isAuthed]);

  const teamById = (id) => teams.find((t) => t.id === id);
  const fixById = (id) => fixtures.find((f) => f.id === id);

  const filteredPicks = useMemo(() => {
    const all = mine?.predictions || [];
    return all.filter((p) => {
      if (historyFilter === "all") return true;
      if (historyFilter === "pending") return !p.scored;
      if (!p.scored) return false;
      if (historyFilter === "exact") return p.points === 3;
      if (historyFilter === "hit") return p.points === 1;
      if (historyFilter === "miss") return (p.points || 0) === 0;
      return true;
    });
  }, [mine, historyFilter]);

  return (
    <div className="space-y-10" data-testid="predictions-root">
      <div>
        <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2">/ FORECASTER LEAGUE</div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Predictions</h1>
        <p className="text-gray-400 mt-2 max-w-xl text-sm">
          3 points for an exact score · 1 point for the correct outcome. Predict every match before kickoff on the Fixtures page.
        </p>
      </div>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        {/* Iter49 · Live streak leaderboard */}
        <PredictionStreakLeaderboard />

        {/* Weekly Top Predictors (last 7 days, posted to Discord every Monday) */}
        <WeeklyTopPredictors />

        {/* Legacy leaderboard */}
        <div className="border border-[#232330] overflow-x-auto">
          <div className="px-5 py-4 border-b border-[#232330] bg-[#0a0a12] flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#F4D03F]" />
            <span className="font-heading font-black uppercase tracking-tight">Top Forecasters</span>
          </div>
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-[#232330]">
                {["#", "Forecaster", "Streak", "Exact", "Hits", "Points"].map((h) => (
                  <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-gray-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {board.map((r) => (
                <tr key={r.user_id} data-testid={`forecaster-${r.rank}`} className="border-b border-[#232330]">
                  <td className="px-4 py-3 font-mono text-gray-500">{String(r.rank).padStart(2, "0")}</td>
                  <td className="px-4 py-3 font-heading font-bold uppercase tracking-tight">{r.name}</td>
                  <td className="px-4 py-3">
                    {r.active_streak > 0 ? (
                      <span
                        data-testid={`streak-${r.rank}`}
                        className={`inline-flex items-center gap-1 font-mono text-xs font-bold px-2 py-0.5 border ${
                          r.active_streak >= 5
                            ? "border-[#FF007F] text-[#FF007F]"
                            : r.active_streak >= 3
                              ? "border-[#F4D03F] text-[#F4D03F]"
                              : "border-cyan-400 text-cyan-400"
                        }`}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                          r.active_streak >= 5 ? "bg-[#FF007F]" : r.active_streak >= 3 ? "bg-[#F4D03F]" : "bg-cyan-400"
                        } animate-pulse`} />
                        {r.active_streak}x
                      </span>
                    ) : (
                      <span className="text-gray-600 font-mono text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-[#F4D03F]">{r.exact}</td>
                  <td className="px-4 py-3 font-mono text-gray-400">{r.correct}/{r.total}</td>
                  <td className="px-4 py-3 font-mono font-bold text-cyan-400 text-base">
                    {r.points}
                    {(r.bonus ?? 0) > 0 && (
                      <span className="ml-1 text-[10px] text-[#F4D03F]">(+{r.bonus})</span>
                    )}
                  </td>
                </tr>
              ))}
              {board.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500 text-sm">
                  No predictions scored yet — get in on the Fixtures page.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* My card */}
        <div className="space-y-4">
          <div className="border border-[#232330] bg-[#101015] p-5">
            <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase mb-3">Your form</div>
            {!isAuthed ? (
              <div className="text-gray-500 text-sm">Log in to play the predictions league.</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="border border-[#232330] p-3">
                    <div className="text-[9px] font-mono tracking-widest text-gray-500 uppercase">Points</div>
                    <div className="font-heading font-black text-2xl text-cyan-400 mt-1">{mine?.totals?.points ?? 0}</div>
                    {(mine?.totals?.bonus ?? 0) > 0 && (
                      <div className="text-[9px] text-[#F4D03F] font-mono mt-0.5">+{mine.totals.bonus} bonus</div>
                    )}
                  </div>
                  <div className="border border-[#232330] p-3">
                    <div className="text-[9px] font-mono tracking-widest text-gray-500 uppercase">Streak</div>
                    <div className="font-heading font-black text-2xl text-[#FF007F] mt-1">
                      {mine?.totals?.active_streak ?? 0}x
                    </div>
                  </div>
                  <div className="border border-[#232330] p-3">
                    <div className="text-[9px] font-mono tracking-widest text-gray-500 uppercase">Exact</div>
                    <div className="font-heading font-black text-2xl text-[#F4D03F] mt-1">{mine?.totals?.exact ?? 0}</div>
                  </div>
                </div>
                <div className="mt-4 text-[10px] font-mono tracking-widest text-gray-500 uppercase">
                  {mine?.totals?.count || 0} predictions submitted
                </div>
                {(mine?.totals?.active_streak ?? 0) >= 3 && (
                  <div className="mt-3 border border-[#F4D03F]/40 bg-[#F4D03F]/5 p-3">
                    <div className="text-[10px] font-mono tracking-widest text-[#F4D03F] uppercase font-bold">
                      🔥 On fire — every future exact adds a bonus point
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {isAuthed && <PredictionBadges />}

          {isAuthed && mine?.predictions?.length > 0 && (
            <div className="border border-[#232330] bg-[#101015] p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase flex items-center gap-2">
                  <Target className="w-3.5 h-3.5 text-cyan-400" /> Pick history
                </div>
                <div className="text-[10px] font-mono text-gray-500">{filteredPicks.length} of {mine.predictions.length}</div>
              </div>
              <div className="flex gap-1 mb-3 flex-wrap" data-testid="history-filter-row">
                {[
                  { k: "all", label: "All" },
                  { k: "exact", label: "Exact" },
                  { k: "hit", label: "Hit" },
                  { k: "miss", label: "Miss" },
                  { k: "pending", label: "Pending" },
                ].map((f) => (
                  <button
                    key={f.k}
                    type="button"
                    data-testid={`history-filter-${f.k}`}
                    onClick={() => setHistoryFilter(f.k)}
                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                      historyFilter === f.k
                        ? "border-cyan-400 text-cyan-400 bg-cyan-400/10"
                        : "border-[#232330] text-gray-500 hover:text-gray-300"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <ul className="space-y-2">
                {(showAll ? filteredPicks : filteredPicks.slice(0, 6)).map((p) => {
                  const f = fixById(p.fixture_id);
                  const h = teamById(f?.home_team_id), a = teamById(f?.away_team_id);
                  return (
                    <li key={p.fixture_id} className="flex items-center justify-between py-2 border-b border-[#232330]/60 last:border-none">
                      <div className="text-xs">
                        <div className="font-heading font-bold uppercase tracking-tight">{h?.tag || "TBD"} vs {a?.tag || "TBD"}</div>
                        <div className="font-mono text-gray-500 mt-0.5">
                          You: <span className="text-white">{p.home_score}–{p.away_score}</span>
                          {p.scored && f && (
                            <> · Actual: <span className="text-white">{f.home_score}–{f.away_score}</span></>
                          )}
                        </div>
                      </div>
                      {p.scored ? (
                        <span className={`font-mono text-xs font-bold px-2 py-0.5 ${
                          p.points === 3 ? "text-[#F4D03F] border border-[#F4D03F]"
                            : p.points === 1 ? "text-cyan-400 border border-cyan-400"
                            : "text-gray-500 border border-[#232330]"
                        }`}>
                          {p.points === 3 ? "EXACT" : p.points === 1 ? "HIT" : "MISS"}
                        </span>
                      ) : (
                        <Trophy className="w-4 h-4 text-gray-600" />
                      )}
                    </li>
                  );
                })}
                {filteredPicks.length === 0 && (
                  <li className="text-[11px] text-gray-500 py-3 font-mono">No picks match this filter.</li>
                )}
              </ul>
              {filteredPicks.length > 6 && (
                <button
                  type="button"
                  data-testid="toggle-full-history-btn"
                  onClick={() => setShowAll((v) => !v)}
                  className="mt-3 w-full font-mono text-[10px] uppercase tracking-widest text-cyan-400 border border-[#232330] hover:border-cyan-400 py-2 transition-colors"
                >
                  {showAll ? "Collapse" : `Show full history (${filteredPicks.length})`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
